/**
 * Expression-internal vocabulary lexicon (foreign surface → English keyword).
 *
 * LokaScript translates COMMAND and ROLE-MARKER vocabulary via the pattern
 * matchers and `joinTokenText` normalization, but the LEAVES of an expression —
 * possessive property names, comparison/existence operators, connectives — are
 * captured verbatim from the source language. When the parse is rendered (or
 * executed) as English, those leaves leak the foreign surface: `put my value`
 * authored as es `poner mi valor` rendered `put my valor` (invalid English) and
 * would read a non-existent `.valor` DOM property at runtime.
 *
 * This module is the reverse lexicon the expression layer consults to normalize
 * those leaves to English, so the rendered/executed expression is canonical. It
 * fixes both production paths at once (foreign→English transpile AND
 * foreign→AST→execute), which is why the normalization happens at PARSE time
 * (see the foreign→English canonical-validity burndown,
 * docs-internal/EXPRESSION_INTERNAL_TRANSLATION_SCOPE.md).
 *
 * Covers possessive property NAMES, expression CONNECTIVES, and the positional
 * LOCATIVE, plus `joinExpressionTokens` — the single seam every raw-expression
 * capture joins through. Surfaces come from the per-language i18n dictionaries
 * (`packages/i18n/src/dictionaries/{code}.ts`) — semantic is upstream of i18n in
 * the build order, so the subset is copied here by
 * `packages/i18n/scripts/extract-property-lexicon.ts`. Regenerate with that
 * script if the dictionaries gain translations. Identity surfaces need no entry:
 * an unlisted one passes through unchanged.
 *
 * The invariant to preserve when extending: translation is ANCHORED to a slot,
 * never applied to a free token, unless the lexicon has vouched for the surface
 * as unambiguous. An expression is mostly code — user variables, string literals,
 * selectors — and a wrong rewrite here is silent.
 */

import type { LanguageToken } from '../../types';
import type { LanguageProfile } from '../../generators/profiles/types';
import { commandSchemas } from '../../generators/command-schemas';
import { getEnglishPossessiveAdjective, getPossessiveReference } from './possessive-keywords';

// prettier-ignore
export const PROPERTY_NAME_LEXICON: Record<string, Record<string, string>> = {
  ar: { "قيمة": "value" },
  bn: { "অক্ষম": "disabled", "চেক করা": "checked", "দৈর্ঘ্য": "length", "মান": "value" },
  de: { "wert": "value" },
  es: { "valor": "value" },
  fr: { "valeur": "value" },
  hi: { "अक्षम": "disabled", "चेक": "checked", "छिपा": "hidden", "मान": "value" },
  id: { "nilai": "value" },
  it: { "valore": "value" },
  ja: { "値": "value" },
  ko: { "값": "value" },
  ms: { "dilumpuhkan": "disabled", "ditanda": "checked", "nilai": "value", "panjang": "length" },
  pl: { "wartość": "value" },
  pt: { "valor": "value" },
  qu: { "chanin": "value" },
  ru: { "значение": "value", "отключено": "disabled", "отмечено": "checked", "скрыто": "hidden" },
  sw: { "thamani": "value" },
  th: { "ความยาว": "length", "ค่า": "value", "ปิดใช้งาน": "disabled", "เลือกแล้ว": "checked" },
  tl: { "haba": "length", "halaga": "value", "hindi_pinagana": "disabled", "naka_tsek": "checked" },
  tr: { "değer": "value" },
  uk: { "вимкнено": "disabled", "значення": "value", "позначено": "checked", "приховано": "hidden" },
  vi: { "ẩn": "hidden", "được chọn": "checked", "giá trị": "value", "vô hiệu": "disabled" },
  zh: { "值": "value" },
};

/**
 * Expression connectives (foreign surface → English), generated by the same
 * script as the property lexicon.
 *
 * Unlike a property name, a connective has no slot to vouch for it — it is
 * translated as a free token — so only surfaces the language does NOT reuse for
 * another sense are carried here. The generator's ambiguity guard drops the
 * collisions: th `เป็น` is both `as` and the copula `is` (translating it would
 * rewrite every Thai `is` condition), and `of` is omitted entirely because most
 * languages spell it the same as `from` (es `de`). `of` is emitted structurally
 * by the of-possessive anchor in {@link joinExpressionTokens} instead.
 *
 * Known gap: hi `के_रूप_में` never matches, because the tokenizer splits
 * underscore-joined surfaces. Harmless (the entry is simply dead), and shared
 * with several dictionary surfaces of the same shape — see the burndown scope doc.
 */
// prettier-ignore
export const CONNECTIVE_LEXICON: Record<string, Record<string, string>> = {
  ar: { "كـ": "as" },
  bn: { "হিসাবে": "as" },
  de: { "als": "as" },
  es: { "como": "as" },
  fr: { "comme": "as" },
  hi: { "के_रूप_में": "as" },
  id: { "sebagai": "as" },
  it: { "come": "as" },
  ja: { "として": "as" },
  ko: { "로": "as" },
  ms: { "sebagai": "as" },
  pl: { "jako": "as" },
  pt: { "como": "as" },
  qu: { "hina": "as" },
  ru: { "как": "as" },
  sw: { "kuwa": "as" },
  tl: { "bilang": "as" },
  tr: { "olarak": "as" },
  uk: { "як": "as" },
  vi: { "như": "as" },
  zh: { "作为": "as" },
};

/**
 * Markers that introduce the owner in a prepositional ("of") possessive, for
 * languages where the marker does NOT tokenize to a `source`/`of` normalized
 * form. These are genitive linkers / particles that surface as bare identifiers
 * or particles (ms `daripada`, sw `ya`, vi `của`, zh `的`), mirroring the i18n
 * dict `of` modifier.
 *
 * Several normalize to OTHER senses (it `di`→tell, pl `z`/uk `з`→style,
 * qu `pa`/tr `nin`→destination), which is exactly why a normalized-form check
 * alone cannot find them. en `of` / ar `من` / ru `из` (→ `source`) and tl `ng`
 * are handled by the literal/normalized check in {@link isOfPossessiveMarker}.
 */
const OF_POSSESSIVE_MARKERS: Record<string, ReadonlySet<string>> = {
  ms: new Set(['daripada']),
  sw: new Set(['ya']),
  vi: new Set(['của']),
  zh: new Set(['的']),
  it: new Set(['di']),
  pl: new Set(['z']),
  uk: new Set(['з']),
  th: new Set(['ของ']),
  // Arabic genitive connector, laam + tatweel. Distinct from the possessive
  // pronoun لي (laam + ya), so listing it here cannot steal that fold.
  ar: new Set(['لـ']),
  ja: new Set(['の']),
  ko: new Set(['의']),
  bn: new Set(['র']),
  hi: new Set(['का']),
  qu: new Set(['pa']),
  tr: new Set(['nin']),
};

/**
 * Whether a token introduces the owner of a prepositional possessive
 * (`value of #price`, `valor de #picker`). Kept narrow, and only consulted when
 * a property head sits before it and a selector after, so it never shadows a
 * real source role.
 */
export function isOfPossessiveMarker(
  profile: LanguageProfile | undefined,
  token: LanguageToken
): boolean {
  const value = token.value.toLowerCase();
  const normalized = (token.normalized ?? '').toLowerCase();
  if (value === 'of' || value === 'ng' || normalized === 'of' || normalized === 'source') {
    return true;
  }
  const langMarkers = profile?.code ? OF_POSSESSIVE_MARKERS[profile.code] : undefined;
  return !!langMarkers && langMarkers.has(value);
}

/**
 * Locative markers inside a positional expression, by the role CONCEPT the
 * tokenizer normalizes them to.
 *
 * Role markers are registered with `normalized: <role name>`, which is right for
 * matching a role but wrong inside an expression: the raw text is read as
 * English, so es `en` (registered as the `destination` marker) emitted the role
 * name and rendered `scroll to last <.message/> destination #chat`. English's own
 * marker never hits this — `in` is not one of its role markers, so it passes
 * through — which is why only non-English languages showed the leak.
 *
 * The slot supplies the sense the role name cannot: a positional source clause is
 * locative (`last <.message/> in #chat`), so `destination` reads `in` and
 * `source` reads `from`. Mapping to the en profile's own `destination` marker
 * instead would wrongly yield `on` (its primary).
 */
const ROLE_CONCEPT_TO_ENGLISH_LOCATIVE: Record<string, string> = {
  destination: 'in',
  source: 'from',
};

/**
 * Every surface a language uses for a "within" preposition, generated from the
 * i18n dict's `in`/`into`/`at`/`within`/`inside` modifiers.
 *
 * These need NO ambiguity guard, unlike {@link CONNECTIVE_LEXICON}: they are read
 * ONLY in the positional expression's locative slot, and the slot fixes the
 * sense. es spelling `in`/`into`/`at` all as `en` is therefore harmless here —
 * in this position every one of them means `in`.
 *
 * Needed because these markers do not all normalize the same way: zh `在`,
 * tr `içinde` and id `dalam` are not role markers at all and leaked verbatim,
 * while pt `dentro` normalizes to `into` — which the canonical parser rejects in
 * this slot.
 */
// prettier-ignore
const LOCATIVE_SURFACES: Record<string, ReadonlySet<string>> = {
  ar: new Set(["عند", "في"]),
  bn: new Set(["এ", "তে"]),
  de: new Set(["bei"]),
  es: new Set(["en"]),
  fr: new Set(["dans", "en", "à"]),
  he: new Set(["לתוך"]),
  hi: new Set(["पर", "में"]),
  id: new Set(["dalam", "di", "ke_dalam"]),
  it: new Set(["a"]),
  ja: new Set(["で", "の中", "へ"]),
  ko: new Set(["안에", "에", "으로"]),
  ms: new Set(["dalam", "di", "ke_dalam"]),
  pl: new Set(["do", "przy", "w"]),
  pt: new Set(["dentro", "em"]),
  qu: new Set(["pi", "ukupi"]),
  ru: new Set(["в", "у"]),
  sw: new Set(["katika", "ndani"]),
  th: new Set(["ที่", "ใน"]),
  tl: new Set(["papasok_sa", "sa", "sa_loob"]),
  tr: new Set(["de", "içinde", "içine"]),
  uk: new Set(["в", "у"]),
  vi: new Set(["trong", "tại", "vào"]),
  zh: new Set(["在", "进入"]),
};

/** English prepositions that mean `in` when they land in the locative slot. */
const ENGLISH_LOCATIVE_SENSES = new Set(['in', 'into', 'at', 'within', 'inside']);

/**
 * Render a positional expression's locative marker as English (`last <.message/>
 * in #chat`). en `in` passes through unchanged — it is not one of English's role
 * markers, which is why only other languages showed a leak here.
 */
export function toEnglishLocative(
  profile: LanguageProfile | undefined,
  marker: LanguageToken
): string {
  const surface = marker.value.toLowerCase();
  const langSurfaces = profile?.code ? LOCATIVE_SURFACES[profile.code] : undefined;
  if (langSurfaces?.has(surface)) return 'in';

  const normalized = marker.normalized ?? marker.value;
  const lowered = normalized.toLowerCase();
  if (ENGLISH_LOCATIVE_SENSES.has(lowered)) return 'in';
  return ROLE_CONCEPT_TO_ENGLISH_LOCATIVE[lowered] ?? normalized;
}

/**
 * Positional query keywords (English + the normalized forms the tokenizers
 * produce for every language, e.g. ar آخر→last, tl huli→last).
 *
 * `closest` is included: `closest <sel>` is the ancestor-scope query form
 * (`hide closest .modal`) and folds to the same call-expression shape the
 * runtime's positional expressions evaluate.
 *
 * Lives here rather than on PatternMatcher because BOTH expression seams need it
 * — the role capture and the raw-expression join (see {@link matchPositionalRun}).
 */
export const POSITIONAL_KEYWORDS: ReadonlySet<string> = new Set([
  'first',
  'last',
  'next',
  'previous',
  'random',
  'closest',
]);

/**
 * Positional heads whose SURFACE doubles as another sense in the profile, so
 * the tokenizer normalizes them away from the positional: bn `শেষ` is
 * registered as `end` (every block terminator) but heads positional runs as
 * `last` — `শেষ <button/> এ .modal` = "last <button/> in .modal" (focus-trap,
 * last-in-collection). Consulted by surface and ONLY inside
 * {@link matchPositionalRun}, and only when the following selector is an
 * ANGLE-BRACKET element query (`<button/>`, `<.message/>`): a bare `.`-class
 * after the head is not enough — behavior-sortable's repeat body captures the
 * block-end `শেষ` adjacent to the next clause's `.{dragClass}` patient, and an
 * unrestricted dual minted a phantom `last` there (`remove last .{dragClass}`,
 * valid but silently wrong). The corpus's positional collection queries all
 * author the element-query form, so the gate costs nothing.
 */
const POSITIONAL_HEAD_DUALS: Record<string, Record<string, string>> = {
  bn: { শেষ: 'last' },
};

/**
 * Normalized command-action keywords (the schema registry's action names).
 * Tokenizers normalize every language's command verbs to these forms, so the set
 * is language-independent. Used to keep a positional source clause from consuming
 * a following command's verb as a locative marker.
 */
export const COMMAND_ACTION_KEYWORDS: ReadonlySet<string> = new Set(
  Object.keys(commandSchemas).map(a => a.toLowerCase())
);

/**
 * One emitted piece of a positional run: the English text, and the token it came
 * from.
 *
 * The TOKEN is carried rather than a bare string because the two callers join
 * differently and only one of them can use a plain space. The positional ROLE
 * capture joins with `' '`; {@link joinExpressionTokens} needs the SOURCE POSITION
 * to decide whether a `.`-prefixed member glues to its object (`<input/>.value`)
 * or stays separated (`I match .active`). A string-only return would silently
 * un-glue every member chain in a condition.
 */
export interface PositionalRunPart {
  readonly text: string;
  readonly token: LanguageToken;
}

export interface PositionalRun {
  readonly parts: readonly PositionalRunPart[];
  /** Tokens consumed, counting from `start`. */
  readonly consumed: number;
}

/**
 * Recognize a positional query run at `tokens[start]`:
 *   `<positional> <selector> [.member]* [<locative-marker> <source-selector>]`
 * e.g. `last <button/> in .modal`, `آخر <.message/> في #chat`.
 *
 * Index-based and NON-CONSUMING so both expression seams can share it: the role
 * capture (`PatternMatcher.tryMatchPositionalExpression`) counts `consumed` off a
 * TokenStream, and the raw-expression join walks an array. Returns null — touching
 * nothing — unless the run's shape is present in full.
 *
 * This run is the ANCHOR that vouches for the locative surface. {@link
 * LOCATIVE_SURFACES} deliberately carries genuinely ambiguous free tokens (tr `de`,
 * pl `do`, es `en`), which are safe ONLY because they are read here, in a slot whose
 * sense is fixed. Never read that table outside this run.
 */
export function matchPositionalRun(
  tokens: readonly LanguageToken[],
  start: number,
  profile: LanguageProfile | undefined
): PositionalRun | null {
  const head = tokens[start];
  if (!head) return null;

  const norm = (head.normalized ?? head.value).toLowerCase();
  const dualNext = tokens[start + 1];
  const dualHead =
    profile?.code && dualNext?.kind === 'selector' && dualNext.value.startsWith('<')
      ? POSITIONAL_HEAD_DUALS[profile.code]?.[head.value.toLowerCase()]
      : undefined;
  if (
    !POSITIONAL_KEYWORDS.has(norm) &&
    !POSITIONAL_KEYWORDS.has(head.value.toLowerCase()) &&
    dualHead === undefined
  ) {
    return null;
  }

  // The captured text is evaluated by the core's English positional expression
  // parser (`next(...)`, `closest(...)`), so the positional KEYWORD must be its
  // normalized English form — a source-language keyword (`cercano`, `次`,
  // `التالي`) is unevaluable as-is and the runtime either errors, drops to `me`,
  // or matches every element.
  const parts: PositionalRunPart[] = [
    { text: dualHead ?? head.normalized ?? head.value, token: head },
  ];
  let i = start + 1;

  // Required: the queried selector (e.g. <.message/>, .message, <button/>).
  const sel = tokens[i];
  if (!sel || sel.kind !== 'selector') return null;
  parts.push({ text: sel.value, token: sel });
  i++;

  // Optional member access on the queried element: `.value`, `.textContent`
  // (`previous <input/>.value`). The tokenizer emits these as `.`-prefixed
  // selector tokens; fold them into the expression so the whole lvalue is one
  // value.
  let memberGuard = 0;
  while (memberGuard++ < 8) {
    const member = tokens[i];
    if (member && member.kind === 'selector' && member.value.startsWith('.')) {
      parts.push({ text: member.value, token: member });
      i++;
    } else {
      break;
    }
  }

  // Optional source clause: <marker> <source-selector> (in #chat / في #chat /
  // sa_loob #chat). The marker may be a keyword, particle, or (TL) identifier;
  // only consumed when a selector follows, so a trailing command keyword is never
  // swallowed.
  const marker = tokens[i];
  const source = tokens[i + 1];
  if (
    marker &&
    source &&
    source.kind === 'selector' &&
    (marker.kind === 'keyword' || marker.kind === 'particle' || marker.kind === 'identifier') &&
    !POSITIONAL_KEYWORDS.has((marker.normalized ?? marker.value).toLowerCase()) &&
    // A command verb is never a locative marker: in a juxtaposed body
    // (`hide closest .modal remove .modal-open from body`) the next clause's verb
    // (`remove`) would otherwise be swallowed as the source marker and the
    // following command lost.
    !COMMAND_ACTION_KEYWORDS.has((marker.normalized ?? marker.value).toLowerCase())
  ) {
    // Marker is a locative keyword/particle (`in`/`from`/في/から) the English
    // runtime reads — normalize it; the source selector is code. Role markers
    // normalize to the ROLE NAME rather than an English word, so es `en` has to be
    // mapped back to the locative sense the slot carries (`in`) — otherwise this
    // renders `last <.message/> destination #chat`.
    parts.push({ text: toEnglishLocative(profile, marker), token: marker });
    parts.push({ text: source.value, token: source });
    i += 2;
  }

  return { parts, consumed: i - start };
}

/**
 * Map an expression connective surface to its English keyword (`como` → `as`).
 * An unlisted surface — already English, or a word the lexicon does not carry —
 * is returned unchanged.
 */
export function translateConnective(languageCode: string, surface: string): string {
  const map = CONNECTIVE_LEXICON[languageCode];
  if (!map) return surface;
  return map[surface.toLowerCase()] ?? surface;
}

/**
 * Map a possessive property surface to its English DOM-property name.
 *
 * Only consulted at a slot already determined to be a property head (inside the
 * possessive matchers), so it is safe: an unlisted surface — an English property
 * (`value`, `innerHTML`), a member-access chain (`.style.display`), or any word
 * with no dictionary entry — is returned unchanged. English (`en`) and any
 * language whose property surface equals English have no table and pass through.
 */
export function translatePropertyName(languageCode: string, surface: string): string {
  const map = PROPERTY_NAME_LEXICON[languageCode];
  if (!map) return surface;
  return map[surface.toLowerCase()] ?? surface;
}

/**
 * Read a token as a possessive, by BOTH its native surface and its normalized
 * form. Profiles key `possessive.keywords` by the native word (en `my`, ar `لي`,
 * ko `내`), but most non-English tokenizers normalize that word before it gets
 * here — so checking only one direction misses half the languages. Mirrors the
 * lookup in `PatternMatcher.tryMatchPossessiveExpression`.
 */
function possessiveReferenceOf(profile: LanguageProfile, token: LanguageToken): string | undefined {
  const candidates = [token.value, token.normalized].filter(Boolean) as string[];
  for (const candidate of candidates) {
    const ref =
      getPossessiveReference(profile, candidate) ??
      getPossessiveReference(profile, candidate.toLowerCase());
    if (ref) return ref;
  }
  return undefined;
}

/**
 * Whether a token can be the bare-word head of an of-possessive. Sigil-prefixed
 * tokens are code the author chose (`$var`, `#id`, `.cls`, `*style`, `:ref`) and
 * grouping/operator tokens are structure — none is a property name.
 */
function isBareWordHead(token: LanguageToken): boolean {
  if (token.kind === 'literal' || token.kind === 'operator' || token.kind === 'punctuation') {
    return false;
  }
  return !/^[$:@#.*^(){}[\]]/.test(token.value);
}

/**
 * Whether a possessive surface ALSO serves as one of the profile's bare
 * references (ms `ia`, qu `chay` are both `it` and `its`). Such a token glued
 * to a `.member` is already the valid `it.prop`; the possessive dot-member
 * branch must decline it so those renders stay byte-identical.
 */
function isAlsoBareReference(profile: LanguageProfile | undefined, token: LanguageToken): boolean {
  const refs = profile?.references;
  if (!refs) return false;
  const surfaces = Object.values(refs)
    .filter((v): v is string => typeof v === 'string')
    .map(v => v.toLowerCase());
  const candidates = [token.value, token.normalized].filter(Boolean) as string[];
  return candidates.some(c => surfaces.includes(c.toLowerCase()));
}

/**
 * Whether a token can be the PROPERTY of a possessive that precedes it.
 *
 * This is what makes the possessive anchor safe, and it is not optional: in
 * several languages the possessive surface and the bare reference are the SAME
 * word (ms `ia`, qu `chay` are both `its` and `it`). Reading such a token as a
 * possessive wherever it appears rewrote `if it` (a truthiness check on the fetch
 * result) into the invalid `if its`. English never shows this — `it` is not among
 * its possessive keywords — so the collision only surfaces in other languages.
 *
 * A possessive is only a possessive when a property actually follows it.
 */
function isPropertyHeadCandidate(token: LanguageToken, languageCode: string): boolean {
  if (!isBareWordHead(token)) return false;
  // A surface the lexicon knows is a property is definitive, whatever its kind
  // (vi `giá trị` tokenizes as one keyword, not an identifier).
  if (translatePropertyName(languageCode, token.value) !== token.value) return true;
  // Otherwise a keyword carries a structural sense — a command verb, connective
  // or marker (ms `tetapkan` = `set`) — and is never a property name.
  return token.kind === 'identifier';
}

/**
 * Deliberately-ambiguous surfaces resolved by LOCAL CONTEXT at the render seam.
 *
 * Each word here was excluded from profile/tokenizer registration because it
 * carries TWO senses and a blanket binding rewrites the other one (the copula
 * slice's named exclusions: ar هو=it/is, hi है=is/has, th เป็น=is/as; the
 * no/without and exists/has dict duals; ja 空, where registering `empty` mints a
 * phantom COMMAND because `empty` is also an ActionType). This seam is the one
 * place where the word and its neighbors are visible together and no pattern
 * generation is involved — a context-gated translation here cannot mint a
 * phantom command and cannot touch the other sense, because nothing is emitted
 * unless the neighbor shape vouches for exactly one reading.
 *
 * Every gate was measured against the full corpus census (every occurrence of
 * every word, probe 2026-07-19): the other-sense occurrences all fail these
 * gates (ar it-sense هو precedes markers/verbs/`هو`, never a predicate; hi
 * not-sense नहीं precedes the keyword फेंकें→throw, never a bare identifier;
 * tl `walang_laman` is a different whole token). A miss leaves the pre-existing
 * behavior byte-identical (keyword-normalized or verbatim fallthrough).
 */
interface AmbiguousSenseRule {
  /** Emitted when the NEXT token is predicate-shaped: a literal (quoted string
      or number) or a keyword normalizing to empty/null/undefined/true/false. */
  beforePredicate?: string;
  /** Emitted when the NEXT token is a conversion type name (`Number`, `json`). */
  beforeTypeName?: string;
  /** Emitted when the NEXT token is a bare ASCII identifier (`dragHandle`) —
      the word shape is required because some tokenizers lex `(`/`)` as
      identifier-kind too. */
  beforeBareIdentifier?: string;
  /** Emitted when the PREVIOUS token is a selector or reference
      (`#modal আছে` → `#modal exists`). */
  afterSubject?: string;
  /** Emitted when the PREVIOUS token is a keyword normalizing to `is`
      (ja `である 空` → `is empty`). */
  afterCopula?: string;
}

const AMBIGUOUS_SENSES: Readonly<Record<string, Readonly<Record<string, AmbiguousSenseRule>>>> = {
  ar: { هو: { beforePredicate: 'is' } },
  hi: { है: { beforePredicate: 'is' }, नहीं: { beforeBareIdentifier: 'no' } },
  th: { เป็น: { beforeTypeName: 'as', beforePredicate: 'is' } },
  ja: { 空: { afterCopula: 'empty' } },
  zh: { 没有: { beforeBareIdentifier: 'no' } },
  tl: { walang: { beforeBareIdentifier: 'no' }, may: { afterSubject: 'exists' } },
  bn: { আছে: { afterSubject: 'exists' } },
  tr: { var: { afterSubject: 'exists' } },
};

/** Predicate values a copula can precede (mirrors the parser's
    CONDITION_PREDICATES plus the boolean literals). */
const SENSE_PREDICATE_NORMALIZED = new Set(['empty', 'null', 'undefined', 'true', 'false']);

/** Canonical `as` conversion targets (both `json` casings occur: `fetch … as
    json` and `… as JSON`). */
const CONVERSION_TYPE_NAMES = new Set([
  'Number',
  'Int',
  'Float',
  'String',
  'Date',
  'Array',
  'Object',
  'JSON',
  'json',
  'FormData',
  'HTML',
  'Fragment',
  'Values',
]);

function resolveAmbiguousSense(
  languageCode: string,
  token: LanguageToken,
  prev: LanguageToken | undefined,
  next: LanguageToken | undefined
): string | undefined {
  const table = AMBIGUOUS_SENSES[languageCode];
  const rule = table?.[token.value] ?? table?.[token.value.toLowerCase()];
  if (!rule) return undefined;
  // Type name beats predicate for th เป็น: `เป็น Number` is a conversion even
  // though `Number` could look identifier-bare; the sets are disjoint anyway.
  if (rule.beforeTypeName && next?.kind === 'identifier' && CONVERSION_TYPE_NAMES.has(next.value)) {
    return rule.beforeTypeName;
  }
  if (
    rule.beforePredicate &&
    next &&
    (next.kind === 'literal' ||
      (next.kind === 'keyword' &&
        SENSE_PREDICATE_NORMALIZED.has((next.normalized ?? '').toLowerCase())))
  ) {
    return rule.beforePredicate;
  }
  if (
    rule.beforeBareIdentifier &&
    next?.kind === 'identifier' &&
    /^[A-Za-z_]\w*$/.test(next.value)
  ) {
    return rule.beforeBareIdentifier;
  }
  if (rule.afterSubject && (prev?.kind === 'selector' || (prev?.kind as string) === 'reference')) {
    return rule.afterSubject;
  }
  if (
    rule.afterCopula &&
    prev?.kind === 'keyword' &&
    (prev.normalized ?? '').toLowerCase() === 'is'
  ) {
    return rule.afterCopula;
  }
  return undefined;
}

/**
 * Join a token slice into the English expression text stored on an `expression`
 * value — the shared seam for every raw-expression capture.
 *
 * Two independent joins used to exist with different rules: the conditional's
 * `joinTokenText` (keyword → `normalized`, else verbatim) and the operator-run
 * join (verbatim, no normalization and no `.`-glue). Both leaked foreign
 * vocabulary into text that the core expression parser then has to read as
 * English, so es `si mi valor es vacío` joined to `if me valor is empty` and
 * `"Hola, " + mi valor` joined to itself unchanged. Routing both here fixes the
 * foreign→English render AND the foreign→AST→execute path, which would otherwise
 * read a non-existent `.valor` DOM property.
 *
 * Translation is ANCHORED, never blanket:
 *   1. a possessive token → the English possessive adjective (`mi` → `my`), and
 *   2. the property head DIRECTLY after it, and only when the lexicon recognizes
 *      the surface (`valor` → `value`);
 *   3. a `<property> <of-marker> <selector>` run → `value of #price` (the
 *      of-marker is emitted structurally, because es spells `of` and `from` the
 *      same `de` — translating that surface freely would corrupt `from` clauses);
 *   4. a positional run → `last <button/> in .modal` ({@link matchPositionalRun},
 *      shared with the role capture in PatternMatcher so the two seams cannot
 *      disagree; the run is what vouches for the ambiguous locative surfaces);
 *   5. a connective the lexicon vouches for as unambiguous (`como` → `as`).
 * Everything else keeps the pre-existing rule. The anchors are what make this
 * safe: a bare `valor` elsewhere may be a user variable, and a string literal may
 * contain any word at all, so nothing is translated without a slot vouching for
 * it — the same guarantee that made the possessive-matcher pass safe (see
 * `translatePropertyName` above).
 */
export function joinExpressionTokens(
  tokens: readonly LanguageToken[],
  profile: LanguageProfile | undefined
): string {
  const languageCode = profile?.code ?? 'en';
  let out = '';
  let previous: LanguageToken | undefined;

  const append = (text: string, token: LanguageToken): void => {
    if (previous === undefined) {
      out = text;
      previous = token;
      return;
    }
    // A member access glues to its object with no space (`event.shiftKey`), but a
    // class selector in a comparison keeps it (`I match .active`). Both surface as
    // a `.`-prefixed token, so disambiguate by SOURCE adjacency. Without this the
    // expression renders `event .shiftKey`, which the canonical parser rejects.
    // A trailing `!` follows the same rule: `beep!` tokenizes as `beep` + `!`, and
    // the canonical parser rejects the spaced `beep !` — glue it only when the two
    // were source-adjacent (a spaced `a != b` keeps its space). Both are gated on
    // adjacency, so the possessive run `my value` (neither `.`- nor `!`-prefixed)
    // still spaces normally.
    const adjacent =
      previous.position?.end !== undefined &&
      token.position?.start !== undefined &&
      previous.position.end === token.position.start;
    const glue = adjacent && (text.startsWith('.') || text.startsWith('!'));
    out += (glue ? '' : ' ') + text;
    previous = token;
  };

  const surfaceOf = (token: LanguageToken): string =>
    token.kind === 'keyword' ? (token.normalized ?? token.value) : token.value;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const reference = profile ? possessiveReferenceOf(profile, token) : undefined;

    const next = tokens[i + 1];
    if (reference !== undefined && next) {
      // A possessor may be separated from its property by a CONNECTOR: id
      // `saya punya nilai` = "I have value" = "my value" (`punya` = "have/own").
      // The value-slot matcher (`tryMatchPossessiveExpression`) already skips the
      // connector via `profile.possessive.connectors`; this seam must too, or the
      // connector is read as the property head and the real property leaks
      // (`saya punya nilai` → `my punya nilai` in every id `if`/`+`/paren
      // expression). No connector → `propIdx` is `i + 1`, byte-identical to before.
      const connectors = profile?.possessive?.connectors;
      const skipsConnector =
        !!connectors?.length &&
        connectors.some(c => {
          const cl = c.toLowerCase();
          return cl === next.value.toLowerCase() || cl === (next.normalized ?? '').toLowerCase();
        });
      const propIdx = skipsConnector ? i + 2 : i + 1;
      const prop = tokens[propIdx];
      if (prop && isPropertyHeadCandidate(prop, languageCode)) {
        append(getEnglishPossessiveAdjective(reference), token);
        append(translatePropertyName(languageCode, prop.value), prop);
        i = propIdx;
        continue;
      }
      // A dot-member can also carry the property when a connector intervenes:
      // qu `chaypaq.error` = chay(→it) + paq(connector) + `.error`. The
      // value-slot matcher accepts the `.`-selector shape (dot stripped →
      // `its error`), but the bare-word guard above declines it, so the
      // connector leaked glued to the member (`if it paq.error`). Source
      // adjacency to the connector is what vouches for member access — the same
      // rule the appender's `.`-glue uses; a spaced `.cls` stays a class
      // selector. Gated on the connector: without one the plain `.`-glue
      // already renders the valid `it.prop`, and this branch must not rewrite it.
      if (
        skipsConnector &&
        prop &&
        /^\.[a-zA-Z_]\w*$/.test(prop.value) &&
        next.position?.end !== undefined &&
        prop.position?.start !== undefined &&
        next.position.end === prop.position.start
      ) {
        append(getEnglishPossessiveAdjective(reference), token);
        append(translatePropertyName(languageCode, prop.value.slice(1)), prop);
        i = propIdx;
        continue;
      }
      // A dot-member can also follow the possessive DIRECTLY (no connector):
      // bn `এর.error` = এর(→its) + source-adjacent `.error` — the plain `.`-glue
      // would leak the untranslatable particle (`if এর.error`). Source adjacency
      // is the same member-access voucher the connector branch above uses (a
      // spaced `.cls` stays a class selector). Additionally gated on the surface
      // NOT doubling as the profile's bare reference: ms `ia` / qu `chay` are
      // both `it` and `its`, and their glued `it.prop` already renders valid —
      // rewriting it would move byte-identical renders for no validity gain.
      // bn's bare `it` is এটি, so এর is unambiguously possessive.
      if (
        !skipsConnector &&
        /^\.[a-zA-Z_]\w*$/.test(next.value) &&
        token.position?.end !== undefined &&
        next.position?.start !== undefined &&
        token.position.end === next.position.start &&
        !isAlsoBareReference(profile, token)
      ) {
        append(getEnglishPossessiveAdjective(reference), token);
        append(translatePropertyName(languageCode, next.value.slice(1)), next);
        i += 1;
        continue;
      }
    }

    // `<property> <of-marker> <selector>` → `value of #price`. Requires the
    // selector to follow, so a real `from`/source clause (which shares the `de`
    // surface in es) is never rewritten.
    const owner = tokens[i + 2];
    if (
      next &&
      owner &&
      owner.kind === 'selector' &&
      isOfPossessiveMarker(profile, next) &&
      isBareWordHead(token)
    ) {
      append(translatePropertyName(languageCode, token.value), token);
      append('of', next);
      append(owner.value, owner);
      i += 2;
      continue;
    }

    // `<positional> <selector> [.member]* [<locative> <selector>]` →
    // `last <button/> in .modal`. The SAME recognizer the positional ROLE capture
    // uses, so the two seams cannot disagree: focus-trap authors that run TWICE —
    // once in the condition, once in the then-branch — and the then-branch (which
    // reaches the recognizer via the pattern matcher) rendered `in` in all 24
    // languages while the condition, joined here, leaked. Two ways, not one:
    // verbatim (ru `в`, zh `在`, tr `içinde` — not role markers at all) and as the
    // ROLE NAME (de/th `in`/`ใน` → `destination`), since role markers are
    // registered with `normalized: <role>`.
    //
    // Deliberately placed AFTER the of-possessive anchor: this way the run can only
    // claim tokens the earlier anchors declined, so no existing rewrite changes. The
    // reverse order would flip `<positional> <sel> <of-marker> <sel>` away from
    // `<sel> of <sel>`, and for a marker absent from LOCATIVE_SURFACES (zh `的`,
    // ja `の`) toEnglishLocative falls through to the verbatim surface — worse than
    // the leak it replaces.
    const run = matchPositionalRun(tokens, i, profile);
    if (run) {
      for (const part of run.parts) append(part.text, part.token);
      i += run.consumed - 1;
      continue;
    }

    // Ambiguous-sense anchor — AFTER the possessive/of/positional anchors (so
    // it can only claim tokens they declined) and before the connective table
    // (these surfaces are deliberately NOT connectives; the sense table is
    // authoritative for them). See AMBIGUOUS_SENSES above for why this seam is
    // the one safe place to translate these words.
    const sense = resolveAmbiguousSense(languageCode, token, tokens[i - 1], next);
    if (sense !== undefined) {
      append(sense, token);
      continue;
    }

    const connective = translateConnective(languageCode, token.value);
    if (connective !== token.value) {
      append(connective, token);
      continue;
    }

    append(surfaceOf(token), token);
  }

  return out.trim();
}
